import { EMPTY, catchError, map } from 'rxjs';
import { useObservableValue } from '../../hooks/useObservable';
import { useGetNewEntityWithTranslatedText } from '../../hooks/useTranslateBackendText';
import { WebSocketStatus } from '../../tokens/socket';
import type { CustomerOrder } from '../../types/CustomerOrder';
import { logger } from '../../utils/logger';
import { useSocketClient, useSocketStatus } from '../WebSocketClientProvider';
import { useWLOrdersProvider } from '../WLOrdersProvider';
import { observeWLOrderByID } from './observeWLOrderByID';

export function useWLOrder({ orderID }: { orderID: string | undefined }): CustomerOrder | undefined {
  const { recentOrdersByOrdID: ordersByOrdID } = useWLOrdersProvider();
  const client = useSocketClient<CustomerOrder>();
  const status = useSocketStatus();

  // Most of the time, the order will be in the ordersByOrdID map.
  // But if it is not, we'll create a subscription to the orderID to try to find it.
  const cachedOrder = orderID ? ordersByOrdID.get(orderID) : undefined;
  const proposedCachedOrder = cachedOrder?.OrderID === orderID ? cachedOrder : undefined;
  const requestedOrderID = proposedCachedOrder ? undefined : orderID;

  const updateOrderWithTranslation = useGetNewEntityWithTranslatedText<CustomerOrder>();

  const fetchedOrder: CustomerOrder | undefined = useObservableValue(() => {
    if (!requestedOrderID || status !== WebSocketStatus.CONNECTED) {
      return EMPTY;
    }
    return observeWLOrderByID(client, requestedOrderID).pipe(
      map(updateOrderWithTranslation),
      catchError(error => {
        logger.error(error);
        return EMPTY;
      })
    );
  }, [client, requestedOrderID, status, updateOrderWithTranslation]);

  const proposedFetchedOrder = orderID === fetchedOrder?.OrderID ? fetchedOrder : undefined;
  return proposedCachedOrder ?? proposedFetchedOrder;
}
